"use client"

import {
  createContext,
  PropsWithChildren,
  RefObject,
  use,
  useMemo,
} from "react"
import { CmdkItemGroup, CmdkPage } from "../types"
import { useLazyRef } from "@seamapi/shared/react/useLazyRef"
import { useMemoizedFn } from "@seamapi/shared/react-hooks"

export interface CmdkContext {
  register(key: string, group: CmdkItemGroup): void
  unregister(key: string): void
  onRegistryUpdated(callback: () => void): () => void
  registeryRef: RefObject<Record<string, CmdkItemGroup>>
  // Live (raw) search query, shared so item registrars outside <Cmdk/> can
  // drive server-side search-as-you-type. Subscribe via onQueryUpdated to
  // avoid re-rendering every consumer on each keystroke.
  queryRef: RefObject<string>
  setQuery(query: string): void
  onQueryUpdated(callback: () => void): () => void
  // Open sub-page, shared like the query so registrars can scope themselves
  // to it (e.g. skip server search off the root page).
  pageRef: RefObject<CmdkPage | null>
  setPage(page: CmdkPage | null): void
  onPageUpdated(callback: () => void): () => void
}

const context = createContext<CmdkContext | null>(null)

export function CmdkProvider({ children }: PropsWithChildren) {
  const registeryRef = useLazyRef<Record<string, CmdkItemGroup>>(() =>
    Object.create(null),
  )
  const listeners = useLazyRef(() => new Set<() => void>()).current

  const register = useMemoizedFn((key: string, group: CmdkItemGroup) => {
    registeryRef.current = { ...registeryRef.current, [key]: group }
    listeners.forEach((callback) => callback())
  })

  const unregister = useMemoizedFn((id: string) => {
    const copy = { ...registeryRef.current }
    delete copy[id]
    registeryRef.current = copy
    listeners.forEach((callback) => callback())
  })

  const onRegistryUpdated = useMemoizedFn((callback: () => void) => {
    listeners.add(callback)

    return () => {
      listeners.delete(callback)
    }
  })

  const queryRef = useLazyRef<string>(() => "")
  const queryListeners = useLazyRef(() => new Set<() => void>()).current

  const setQuery = useMemoizedFn((query: string) => {
    queryRef.current = query
    queryListeners.forEach((callback) => callback())
  })

  const onQueryUpdated = useMemoizedFn((callback: () => void) => {
    queryListeners.add(callback)

    return () => {
      queryListeners.delete(callback)
    }
  })

  const pageRef = useLazyRef<CmdkPage | null>(() => null)
  const pageListeners = useLazyRef(() => new Set<() => void>()).current

  // Switching pages always starts from an empty query.
  const setPage = useMemoizedFn((page: CmdkPage | null) => {
    pageRef.current = page
    setQuery("")
    pageListeners.forEach((callback) => callback())
  })

  const onPageUpdated = useMemoizedFn((callback: () => void) => {
    pageListeners.add(callback)

    return () => {
      pageListeners.delete(callback)
    }
  })

  const value = useMemo(
    () => ({
      register,
      unregister,
      onRegistryUpdated,
      registeryRef,
      queryRef,
      setQuery,
      onQueryUpdated,
      pageRef,
      setPage,
      onPageUpdated,
    }),
    [
      register,
      unregister,
      onRegistryUpdated,
      registeryRef,
      queryRef,
      setQuery,
      onQueryUpdated,
      pageRef,
      setPage,
      onPageUpdated,
    ],
  )

  return <context.Provider value={value}>{children}</context.Provider>
}

export function useCmdkContext(): CmdkContext {
  return use(context) as CmdkContext
}
